/* ============================================================
   Sonder Atelier — theme interactions (vanilla JS)
   Header scroll state, mobile menu, reveal-on-scroll, quantity
   steppers, product variant selection, and AJAX cart backed by
   Shopify's native /cart endpoints.
   ============================================================ */
(function () {
  "use strict";

  var theme = window.theme || {};
  var routes = theme.routes || {
    cart: "/cart",
    cartAdd: "/cart/add",
    cartChange: "/cart/change",
  };

  /* ---------- Money formatting (Shopify money_format) ---------- */
  function formatMoney(cents) {
    var value = (cents / 100).toFixed(2);
    var format = theme.moneyFormat || "${{amount}}";
    var parts = value.split(".");
    var dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    var withDecimal = dollars + "." + parts[1];
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, withDecimal)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, dollars)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withDecimal.replace(".", ","))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, dollars);
  }
  theme.formatMoney = formatMoney;

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  /* ============================================================
     Header: transparent over hero, solid after scroll
     ============================================================ */
  function initHeader() {
    var header = document.querySelector(".site-header");
    if (!header) return;
    if (!document.body.classList.contains("header-transparent")) return;

    var hero = document.querySelector(".video-hero");
    var trigger = hero
      ? function () { return hero.offsetHeight - header.offsetHeight; }
      : function () { return 40; };

    function onScroll() {
      if (window.scrollY > trigger()) header.classList.add("is-solid");
      else header.classList.remove("is-solid");
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  /* ============================================================
     Mobile menu
     ============================================================ */
  function initMobileMenu() {
    var toggle = document.querySelector("[data-menu-open]");
    var menu = document.getElementById("mobile-menu");
    if (!toggle || !menu) return;
    var closeBtn = menu.querySelector("[data-menu-close]");

    function open() {
      menu.classList.add("is-open");
      menu.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";
      if (closeBtn) closeBtn.focus();
    }
    function close() {
      menu.classList.remove("is-open");
      menu.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
      toggle.focus();
    }
    toggle.addEventListener("click", open);
    if (closeBtn) closeBtn.addEventListener("click", close);
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && menu.classList.contains("is-open")) close();
    });
    menu.querySelectorAll("a").forEach(function (a) {
      a.addEventListener("click", close);
    });
  }

  /* ============================================================
     Reveal on scroll
     ============================================================ */
  function initReveal() {
    var els = document.querySelectorAll(".reveal");
    if (!("IntersectionObserver" in window) || !els.length) {
      els.forEach(function (el) { el.classList.add("is-visible"); });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "-60px 0px" }
    );
    els.forEach(function (el) { io.observe(el); });
  }

  /* ============================================================
     Quantity steppers (generic)
     ============================================================ */
  function initQtySteppers(scope) {
    (scope || document).querySelectorAll("[data-qty]").forEach(function (wrap) {
      if (wrap.dataset.qtyBound) return;
      wrap.dataset.qtyBound = "1";
      var input = wrap.querySelector("input");
      wrap.querySelectorAll("[data-qty-step]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var step = parseInt(btn.getAttribute("data-qty-step"), 10);
          var min = parseInt(input.min || "1", 10);
          var next = (parseInt(input.value, 10) || min) + step;
          if (next < min) next = min;
          input.value = next;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      });
    });
  }

  /* ============================================================
     Cart count refresh
     ============================================================ */
  function refreshCartCount() {
    fetch(routes.cart + ".js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { updateCartCount(cart.item_count); })
      .catch(function () {});
  }
  function updateCartCount(count) {
    document.querySelectorAll("[data-cart-count]").forEach(function (el) {
      el.textContent = count;
      el.classList.toggle("hidden", count === 0);
    });
  }

  /* ============================================================
     Product: variant selection + add to cart
     ============================================================ */
  function initProductForm() {
    var form = document.querySelector("[data-product-form]");
    if (!form) return;

    var dataEl = document.getElementById(form.getAttribute("data-variants"));
    var variants = [];
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { variants = []; }

    var idInput = form.querySelector("[data-variant-id]");
    var priceEl = document.querySelector("[data-product-price]");
    var addBtn = form.querySelector("[data-add-button]");
    var addLabel = addBtn ? addBtn.querySelector("[data-add-label]") : null;
    var selected = {};

    // Initialise from currently-selected option buttons
    form.querySelectorAll("[data-option-index]").forEach(function (group) {
      var idx = group.getAttribute("data-option-index");
      var active = group.querySelector(".option__btn.is-selected");
      if (active) selected[idx] = active.getAttribute("data-value");
    });

    function findVariant() {
      return variants.find(function (v) {
        return v.options.every(function (opt, i) {
          return selected[i] === opt;
        });
      });
    }

    function updateMedia(variant) {
      if (!variant || variant.featured_media == null) return;
      var thumb = document.querySelector(
        '[data-media-id="' + variant.featured_media.id + '"]'
      );
      if (thumb) thumb.click();
    }

    function render() {
      var variant = findVariant();
      if (!variant) {
        if (addBtn) { addBtn.disabled = true; }
        if (addLabel) addLabel.textContent = "Unavailable";
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (priceEl) {
        var html = formatMoney(variant.price);
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          html =
            '<span class="price--on-sale"><span class="price__was">' +
            formatMoney(variant.compare_at_price) +
            "</span>" +
            formatMoney(variant.price) +
            '<span class="price__badge">Sale</span></span>';
        }
        priceEl.innerHTML = html;
      }
      if (addBtn && addLabel) {
        if (variant.available) {
          addBtn.disabled = false;
          addLabel.textContent = "Add to cart";
        } else {
          addBtn.disabled = true;
          addLabel.textContent = "Sold out";
        }
      }
      var url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url.toString());
      updateMedia(variant);
    }

    form.querySelectorAll("[data-option-index]").forEach(function (group) {
      var idx = group.getAttribute("data-option-index");
      group.querySelectorAll(".option__btn").forEach(function (btn) {
        btn.addEventListener("click", function () {
          group.querySelectorAll(".option__btn").forEach(function (b) {
            b.classList.remove("is-selected");
          });
          btn.classList.add("is-selected");
          selected[idx] = btn.getAttribute("data-value");
          render();
        });
      });
    });

    render();

    // Add to cart (AJAX -> native cart)
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      if (!idInput || !idInput.value) return;
      if (addLabel) addLabel.textContent = "Adding…";
      var body = new FormData(form);
      fetch(routes.cartAdd + ".js", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: body,
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (j) { throw j; });
          return r.json();
        })
        .then(function () {
          if (addLabel) addLabel.textContent = "Added ✓";
          refreshCartCount();
          setTimeout(function () {
            if (addLabel) addLabel.textContent = "Add to cart";
          }, 2200);
        })
        .catch(function (err) {
          if (addLabel) addLabel.textContent = (err && err.description) || "Unavailable";
          setTimeout(function () {
            if (addLabel) addLabel.textContent = "Add to cart";
          }, 2600);
        });
    });
  }

  /* ============================================================
     Product gallery thumbnails
     ============================================================ */
  function initGallery() {
    var main = document.querySelector("[data-gallery-main]");
    if (!main) return;
    document.querySelectorAll("[data-gallery-thumb]").forEach(function (thumb) {
      thumb.addEventListener("click", function () {
        var src = thumb.getAttribute("data-src");
        var alt = thumb.getAttribute("data-alt") || "";
        if (src) { main.src = src; main.alt = alt; }
        document.querySelectorAll("[data-gallery-thumb]").forEach(function (t) {
          t.classList.remove("is-active");
        });
        thumb.classList.add("is-active");
      });
    });
  }

  /* ============================================================
     Cart page: live quantity + remove
     ============================================================ */
  function initCart() {
    var cartRoot = document.querySelector("[data-cart]");
    if (!cartRoot) return;

    function changeLine(key, quantity) {
      cartRoot.classList.add("is-busy");
      fetch(routes.cartChange + ".js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity: quantity }),
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (cart.item_count === 0) { window.location.reload(); return; }
          var row = cartRoot.querySelector('[data-line-key="' + key + '"]');
          if (quantity === 0 && row) {
            row.remove();
          } else if (row) {
            var item = cart.items.filter(function (i) { return i.key === key; })[0];
            if (item) {
              var lp = row.querySelector("[data-line-price]");
              if (lp) lp.textContent = formatMoney(item.final_line_price);
              var qi = row.querySelector("input");
              if (qi) qi.value = item.quantity;
            }
          }
          var sub = cartRoot.querySelector("[data-cart-subtotal]");
          if (sub) sub.textContent = formatMoney(cart.items_subtotal_price);
          var tot = cartRoot.querySelector("[data-cart-total]");
          if (tot) tot.textContent = formatMoney(cart.total_price);
          updateCartCount(cart.item_count);
          cartRoot.classList.remove("is-busy");
        })
        .catch(function () { window.location.reload(); });
    }

    cartRoot.querySelectorAll("[data-line-key]").forEach(function (row) {
      var key = row.getAttribute("data-line-key");
      var input = row.querySelector("input");
      if (input) {
        input.addEventListener("change", function () {
          var q = parseInt(input.value, 10);
          if (isNaN(q) || q < 0) q = 0;
          changeLine(key, q);
        });
      }
      var remove = row.querySelector("[data-line-remove]");
      if (remove) {
        remove.addEventListener("click", function (e) {
          e.preventDefault();
          changeLine(key, 0);
        });
      }
    });
  }

  /* ============================================================
     Collection facets: auto-submit
     ============================================================ */
  function initFacets() {
    var form = document.querySelector("[data-facet-form]");
    if (form) {
      form.querySelectorAll('input[type="checkbox"], select').forEach(function (el) {
        el.addEventListener("change", function () { form.submit(); });
      });
    }
    document.querySelectorAll("[data-sort]").forEach(function (sel) {
      sel.addEventListener("change", function () {
        var url = new URL(window.location.href);
        url.searchParams.set("sort_by", sel.value);
        window.location.href = url.toString();
      });
    });
  }

  ready(function () {
    initHeader();
    initMobileMenu();
    initReveal();
    initQtySteppers(document);
    initProductForm();
    initGallery();
    initCart();
    initFacets();
  });
})();
